import styled from '@emotion/styled'
import { FC, useEffect } from 'react'

import {
  Fees,
  FieldSet,
  FormItem,
  Label,
  LegacyDatePicker,
  RadioWithLabel,
  Section,
  Select,
  Space,
  TextInput,
  Typography,
  ValidationMessage
} from '../../../../../components/core-v2'
import { useUser } from '../../../../../utilities/hooks/useUser'
import { getUserDisplayInfo, isCollateralUser } from '../../../../tradebuilder-v2/util/user'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { StyledCharges, StyledValue } from '../existing-note/details-summary/primitives'
import {
  init,
  setBusinessUnit,
  setClient,
  setCollateral,
  setMaturityDate,
  setMaturityDateType,
  setNoteName,
  setStartDate
} from './new-note-slice'
import { StyledSection } from './primitives'

// TODO: Get rid of this, not necessary - just replace with date?.toString()
const safeGetDateAsString = (date?: Date) => {
  try {
    const dateString = date.toString()
    return dateString
  } catch {
    return undefined
  }
}

const StyledAttributeBlock = styled.div`
  border-bottom: dotted 1px var(--White90);
  &:first-of-type {
    border-top: none;
  }

  &:last-of-type {
    border-bottom: none;
  }
`

export const NewNote: FC = () => {
  const dispatch = useAppDispatch()
  const state = useAppSelector(x => x.newNote)

  const { data: user } = useUser()
  const userInfo = getUserDisplayInfo(user)
  const { userRoles, userType } = userInfo
  const collateralUser = isCollateralUser(userRoles)

  useEffect(() => {
    dispatch(init(!collateralUser))
  }, [dispatch, collateralUser])

  return (
    <Section pb={6} type="primary">
      <Section data-testid="new-note-form" mt={6} mx={5} pb={3} pt={6} px={5} type="secondary">
        {(userType === 'admin' || userType === 'absa') && (
          <div data-private>
            <FormItem>
              <Label>Client</Label>
              <Select
                data-testid="client"
                isError={!!state.errors.client}
                value={state.note.client?.clientId}
                onChange={e => dispatch(setClient(e.target.value))}
              >
                <option value="">Select a client</option>
                {state.clients?.map(c => (
                  <option key={c.clientName} value={c.clientId}>
                    {c.clientName}
                  </option>
                ))}
              </Select>
              <ValidationMessage errors={state.errors} fieldName="client" />
            </FormItem>
            <FormItem>
              <Label>Business unit</Label>
              <Select
                data-testid="businessUnit"
                disabled={state.note?.client === undefined}
                isError={!!state.errors.businessUnit}
                value={state.note.businessUnitId}
                onChange={e => dispatch(setBusinessUnit(e.target.value))}
              >
                <option value="">Select a business unit</option>
                {state.currentBusinessUnits?.map(bu => (
                  <option key={bu.businessUnitName} value={bu.businessUnitId}>
                    {bu.businessUnitName}
                  </option>
                ))}
              </Select>
              <ValidationMessage errors={state.errors} fieldName="businessUnit" />
            </FormItem>
          </div>
        )}
        {userType === 'business-unit' && state.note.client && (
          <Space mb={6}>
            <StyledAttributeBlock>
              <Space isflex alignItems="center" justifyContent="space-between" py={3}>
                <Typography fontSize="13px" fontWeight={800} letterSpacing="0" variant="b3">
                  Client
                </Typography>
                <StyledValue data-private>{state.note.client.clientName}</StyledValue>
              </Space>
            </StyledAttributeBlock>
            <StyledAttributeBlock>
              <Space isflex alignItems="center" justifyContent="space-between" py={3}>
                <Typography fontSize="13px" fontWeight={800} letterSpacing="0" variant="b3">
                  Business unit
                </Typography>
                <StyledValue data-private>{state.note.businessUnit.businessUnitName}</StyledValue>
              </Space>
            </StyledAttributeBlock>
          </Space>
        )}

        <FormItem>
          <Label>Name</Label>
          <TextInput
            data-testid="noteName"
            isError={!!state.errors.noteName}
            maxLength={64}
            value={state.note.noteName}
            onChange={e => {
              dispatch(setNoteName(e.target.value))
            }}
          />
          <ValidationMessage errors={state.errors} fieldName="noteName" />
        </FormItem>
        <FormItem>
          <Label>Start date</Label>
          <LegacyDatePicker
            shouldAllowPublicHolidays={false}
            testId="startDate"
            value={new Date(state.note.startDate)}
            onChange={date => dispatch(setStartDate(safeGetDateAsString(date)))}
          />
          <ValidationMessage errors={state.errors} fieldName="startDate" />
        </FormItem>
        <FormItem>
          <Label>Maturity date</Label>
          <StyledSection direction="vertical">
            <FieldSet direction="vertical">
              <RadioWithLabel
                checked={state.maturityDateType === 'perpetual'}
                data-testid="maturityDateType_perpetual"
                onChange={() => dispatch(setMaturityDateType('perpetual'))}
              >
                Perpetual
              </RadioWithLabel>
              <RadioWithLabel
                checked={state.maturityDateType === 'specific'}
                data-testid="maturityDateType_specific"
                onChange={() => dispatch(setMaturityDateType('specific'))}
              >
                Choose a maturity date
              </RadioWithLabel>
            </FieldSet>
            {state.maturityDateType === 'specific' && (
              <LegacyDatePicker
                shouldAllowPublicHolidays={false}
                testId="endDate"
                value={new Date(state.note.endDate)}
                onChange={date => dispatch(setMaturityDate(safeGetDateAsString(date)))}
              />
            )}
          </StyledSection>
          <ValidationMessage errors={state.errors} fieldName="endDate" />
        </FormItem>
        {collateralUser && (
          <FormItem>
            <Label>Collateral for loan</Label>
            <StyledSection direction="vertical">
              <FieldSet direction="vertical">
                <RadioWithLabel
                  checked={state.isCollateralSet && state.note.isCollateralForLoan}
                  data-testid="isCollateralForLoan_yes"
                  onChange={() => dispatch(setCollateral(true))}
                >
                  Yes
                </RadioWithLabel>
                <RadioWithLabel
                  checked={state.isCollateralSet && !state.note.isCollateralForLoan}
                  data-testid="isCollateralForLoan_no"
                  onChange={() => dispatch(setCollateral(false))}
                >
                  No
                </RadioWithLabel>
              </FieldSet>
            </StyledSection>
            <ValidationMessage errors={state.errors} fieldName="endDate" />
          </FormItem>
        )}
        <Space mb={6}>
          <StyledAttributeBlock>
            <Space isflex alignItems="center" justifyContent="space-between" py={3}>
              <Typography fontSize="13px" fontWeight={800} letterSpacing="0" variant="b3">
                Distribution processing
              </Typography>
              <StyledValue>{state.note.dividendProcessing}</StyledValue>
            </Space>
          </StyledAttributeBlock>
        </Space>

        {state.fees && (
          <Space mb={5} mt={6}>
            <StyledCharges>
              <Fees fees={state.fees} title="Note charges" />
            </StyledCharges>
          </Space>
        )}
      </Section>
    </Section>
  )
}
